import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import * as R from 'ramda'

import { RxType } from '~/api/graphql/generated/types'
import RxRequestCatalogItemNotLinked from '~/components/dashboard/prescription/prescription-editor/RxRequestCatalogItemNotLinked'
import VetDietInfo from '~/components/dashboard/prescription/prescription-editor/VetDietInfo'
import FeatureToggle from '~/constants/featureToggle'
import {
  getFeatureToggle,
  getPrescriptionProductSizeUnit,
} from '~/store/reducers/constants'
import type { Prescription } from '~/types'
import { getHighValue } from '~/utils/invoiceUtils'
import { toSentenceCase } from '~/utils/sentenceCase'

import styles from './QuantityAndRefills.module.scss'

export const QUANTITY_AND_REFILLS_DEFAULT_SEPARATOR = ' | '

interface QuantityAndRefillsProps {
  isVetDiet: boolean
  prescription: Prescription
  refillSeparator?: string
  /** When true, applies {@link toSentenceCase} to quantity+refill copy (non-vet: whole line; vet diet: refill segment only). */
  sentenceCase?: boolean
}

const QuantityAndRefills = ({
  prescription,
  isVetDiet,
  sentenceCase = false,
  refillSeparator = QUANTITY_AND_REFILLS_DEFAULT_SEPARATOR,
}: QuantityAndRefillsProps) => {
  const { t } = useTranslation(['Plurals', 'Clients'])

  const prescriptionProductSizeUnit = useSelector(
    getPrescriptionProductSizeUnit,
  )
  const isFoodCatalogEnabled = useSelector(
    getFeatureToggle(FeatureToggle.FOOD_CATALOG),
  )

  const getCatalogItemKey = () => {
    if (isFoodCatalogEnabled) {
      return 'globalVariationMappingId'
    }
    if (prescription.rxType === RxType.VetDiet) {
      return 'globalFoodCatalogVariationId'
    }
    return 'globalVariationId'
  }

  const isChewyCatalogItemNotLinked = R.isNil(prescription[getCatalogItemKey()])

  const { quantityNumbRefills } = prescription

  const quantity =
    prescription.totalQuantity ?? getHighValue(prescription.quantity) ?? 0

  const unitOfProduct = prescriptionProductSizeUnit.find(
    (unit) => unit.id === prescription.totalQuantityUnitId,
  )

  const vetDietDescription = (
    <>
      {`${quantity} `}
      {isChewyCatalogItemNotLinked ? (
        <RxRequestCatalogItemNotLinked sx={{ mb: 2 }} />
      ) : (
        <VetDietInfo drugInfo={prescription.drugInfo} />
      )}
    </>
  )

  const quantityText = unitOfProduct?.name
    ? `${quantity} ${unitOfProduct.name}`
    : `${quantity}`

  const refillCore =
    quantityNumbRefills && quantityNumbRefills > 0
      ? t('Plurals:Z_ICU_WORKAROUND.NUMBER_REFILLS', {
          number: quantityNumbRefills,
        })
      : t('Clients:TIMELINE.PRESCRIPTION_CARD.NO_REFILLS')

  const refillText = `${refillSeparator}${refillCore}`

  if (!isVetDiet) {
    const quantityLine = `${quantityText}${refillText}`
    return (
      <div className={styles.container}>
        {sentenceCase ? toSentenceCase(quantityLine) : quantityLine}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {vetDietDescription}
      {sentenceCase
        ? `${refillSeparator}${toSentenceCase(refillCore)}`
        : refillText}
    </div>
  )
}

export default QuantityAndRefills
